import { useEffect } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { NotesTextEditor } from '@factorialco/f0-react/dist/experimental'
import { NotesTextEditorHandle } from '@factorialco/f0-react/dist/experimental'
import { LockLocked } from '@factorialco/f0-react/icons/app'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { MeetingSkeleton } from 'modules/meetings/components/Skeletons/MeetingSkeleton'
import { useMeeting } from 'modules/meetings/lib/hooks/useMeeting'
import { useNotesTextEditorConfig } from 'modules/meetings/lib/hooks/useNotesTextEditorConfig'
import { formatTimeAgo } from 'modules/meetings/lib/utils/dates'

interface MeetingNotesProps {
  textEditorRef: React.RefObject<NotesTextEditorHandle> | null | undefined
  meetingState: 'pre' | 'post' | null | undefined
  haveTranscript?: boolean
  hideNotes?: boolean
}

export const MeetingNotes = ({
  textEditorRef,
  meetingState,
  haveTranscript = false,
  hideNotes = false,
}: MeetingNotesProps) => {
  const { page, loading, meeting, setNotes, notes, meetingId } = useMeeting()

  const {
    access: { locale },
  } = useCurrentSession()

  const { labels, aiBlockConfig } = useNotesTextEditorConfig({
    meetingId: meetingId ?? undefined,
    meetingState: meetingState ?? undefined,
  })

  const isCorrectMeetingLoaded = meeting?.id === Number(meetingId)
  const isStillLoading =
    loading || !page || notes === null || !meetingState || !isCorrectMeetingLoaded

  useEffect(() => {
    if (!isStillLoading && textEditorRef?.current) {
      textEditorRef.current.focus()
    }
  }, [isStillLoading, textEditorRef])

  if (isStillLoading) return <MeetingSkeleton withPadding />

  return (
    <Box
      overflowY="auto"
      gap="s16"
      height="full"
      flexGrow
      width="full"
      style={{
        cursor: 'text',
        display: hideNotes ? 'none' : undefined,
      }}
    >
      <Box overflowY="hidden" height="full">
        <NotesTextEditor
          key={meetingId}
          ref={textEditorRef}
          labels={labels}
          placeholder={i18n.t('meetings.notes.placeholder')}
          onChange={({ json }) => {
            const jsonString = JSON.stringify(json)
            setNotes(jsonString)
          }}
          initialEditorState={{
            content: notes && notes.trim() !== '' ? JSON.parse(notes) : undefined,
            title: page?.title,
          }}
          aiBlockConfig={aiBlockConfig}
          withPadding
          metadata={[
            {
              type: 'tag',
              label: i18n.t('meetings.notes.private_notes'),
              icon: LockLocked,
            },
            {
              type: 'text',
              label: i18n.t('meetings.notes.last_edited'),
              content: formatTimeAgo(page?.updatedAt, locale),
            },
          ]}
        />
      </Box>
    </Box>
  )
}
